import { useEffect, useCallback, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';

/**
 * Vehicle Path Initializer Hook
 * Responsible for handling path initialization and restoration from external path data
 */
export const useVehiclePathInitializer = ({
  vehiclePaths,
  setVehiclePaths,
  vehicleCount,
  locations,
  startId,
  path,
  getVehicleColor,
  updateUsedColors,
  selectedVehicleIndex,
  setSelectedVehicleIndex,
  setMapPointSelect,
  activeCallbackIndexRef,
  setPathComplete
}) => {
  // Use ref to track current vehiclePaths without causing dependency cycles
  const vehiclePathsRef = useRef(vehiclePaths);
  vehiclePathsRef.current = vehiclePaths;

  // Helper function to find location by ID
  const findLocationById = useCallback((id) => {
    return locations.find(loc => loc.id === id);
  }, [locations]);

  // Initialize paths based on vehicle count - simplified logic
  useEffect(() => {
    // Only process vehicle count changes, ignore other triggers
    if (vehiclePaths.length !== vehicleCount) {
      // Create new vehicle paths array
      const newVehiclePaths = Array(vehicleCount).fill(null).map((_, index) => {
        // Preserve existing vehicle data
        if (index < vehiclePaths.length && vehiclePaths[index]) {
          return { ...vehiclePaths[index] };
        }
        
        // Create new vehicle with start point
        const color = getVehicleColor(index + 1);
        return {
          id: uuidv4(),
          path: startId ? [findLocationById(startId)] : [],
          color: color,
          distance: 0
        };
      });
      
      setVehiclePaths(newVehiclePaths);
      
      // Reset selection if out of range
      if (selectedVehicleIndex !== null && selectedVehicleIndex >= vehicleCount) {
        setSelectedVehicleIndex(null);
        if (setMapPointSelect) {
          setMapPointSelect(null);
          activeCallbackIndexRef.current = null;
        }
      }
    }
  }, [vehicleCount, vehiclePaths.length, startId, getVehicleColor, findLocationById, setVehiclePaths, selectedVehicleIndex, setSelectedVehicleIndex, setMapPointSelect]); 
  
  // Initialize vehicle paths from external path
  useEffect(() => {
    // Only process if there's path data and vehiclePaths is empty or uninitialized
    // Prevent overwriting user's current editing progress
    if (path && typeof path === 'object') {
      // Check if there's vehiclePaths metadata
      if (path.vehiclePaths && Array.isArray(path.vehiclePaths) && path.vehiclePaths.length > 0) {
        // Only initialize if current vehiclePaths is empty or only contains start points
        const hasUserEditedPaths = vehiclePathsRef.current.some(vehicle => 
          vehicle.path && vehicle.path.length > 1
        );
        
        if (hasUserEditedPaths) {
          // User has already edited paths, don't overwrite
          return;
        }
        
        // Track colors that will be used
        const pathColors = new Set();
        path.vehiclePaths.forEach(vehicle => {
          if (vehicle.color) {
            pathColors.add(vehicle.color);
          }
        });
        
        // Use timeout to prevent render phase updates
        setTimeout(() => {
          updateUsedColors(pathColors);
          setVehiclePaths(path.vehiclePaths);
          setPathComplete(true);
        }, 0);
        return;
      }
    }
    
    // If there's a normal path array and vehiclePaths is uninitialized, try to assign points to vehicles
    if (Array.isArray(path) && path.length > 0 && 
        (vehiclePathsRef.current.length === 0 || vehiclePathsRef.current.every(v => v.path.length <= 1))) {
      
      // Helper function to distribute path points to vehicles
      const distributePathPoints = () => {
        // Create vehicle paths if not enough
        let newVehiclePaths = [...vehiclePathsRef.current];
        
        const startPoint = findLocationById(startId);
        if (!startPoint) return newVehiclePaths;
        
        const pathPoints = path.map(id => findLocationById(id)).filter(Boolean);
        
        // If there are path points, distribute them
        if (pathPoints.length > 0) {
          // Calculate approximate points per vehicle
          const pointsPerVehicle = Math.ceil(pathPoints.length / vehicleCount);
          
          // Assign points to each vehicle
          for (let i = 0; i < vehicleCount && i < newVehiclePaths.length; i++) {
            const vehiclePoints = [startPoint];
            
            // Calculate point range for this vehicle
            const startIdx = i * pointsPerVehicle;
            const endIdx = Math.min((i + 1) * pointsPerVehicle, pathPoints.length);
            
            // Add assigned points
            for (let j = startIdx; j < endIdx; j++) {
              if (pathPoints[j] && pathPoints[j].id !== startId) {
                vehiclePoints.push(pathPoints[j]);
              }
            }
            
            // If path has points and doesn't end with start point, add start point as end
            if (vehiclePoints.length > 1 && vehiclePoints[vehiclePoints.length - 1].id !== startId) {
              vehiclePoints.push(startPoint);
            }
            
            // Update vehicle path
            newVehiclePaths[i] = {
              ...newVehiclePaths[i],
              path: vehiclePoints
            };
          }
        }
        
        return newVehiclePaths;
      };
      
      // If not enough vehicle paths, create them first
      if (vehiclePathsRef.current.length < vehicleCount) {
        // Add missing vehicle paths
        const additionalPaths = Array(vehicleCount - vehiclePathsRef.current.length)
          .fill(null)
          .map((_, index) => {
            const color = getVehicleColor(vehiclePathsRef.current.length + index + 1);
            return {
              id: uuidv4(),
              path: startId ? [findLocationById(startId)] : [],
              color: color
            };
          });
        
        const updatedPaths = [...vehiclePathsRef.current, ...additionalPaths];
        setVehiclePaths(updatedPaths);
        
        // Delay point distribution to ensure vehiclePaths is updated
        setTimeout(() => {
          const distributedPaths = distributePathPoints();
          setVehiclePaths(distributedPaths);
          setPathComplete(true);
        }, 0);
      } else {
        // Directly distribute path points
        const distributedPaths = distributePathPoints();
        setVehiclePaths(distributedPaths);
        setPathComplete(true);
      }
    }
  }, [path, locations, startId, vehicleCount, getVehicleColor, 
      updateUsedColors, setVehiclePaths, setPathComplete, findLocationById]);
  
  // When start point changes, update start point for all vehicle paths
  useEffect(() => {
    if (!startId || locations.length === 0) return;
    
    const startPoint = findLocationById(startId);
    if (!startPoint) return;
    
    // Update start point for all vehicle paths
    setVehiclePaths(prev => prev.map(vehicle => {
      // If path is empty, directly add start point
      if (vehicle.path.length === 0) {
        return { ...vehicle, path: [startPoint] };
      }
      
      // If first point is already start point, no change needed
      if (vehicle.path[0] && vehicle.path[0].id === startId) {
        return vehicle;
      }
      
      // If path contains start point, move it to first position
      const startPointIndex = vehicle.path.findIndex(p => p && p.id === startId);
      if (startPointIndex > 0) {
        return {
          ...vehicle,
          path: [
            startPoint,
            ...vehicle.path.slice(0, startPointIndex),
            ...vehicle.path.slice(startPointIndex + 1)
          ]
        };
      }
      
      // If no start point in path, add it at beginning
      return {
        ...vehicle,
        path: [startPoint, ...vehicle.path]
      };
    }));
  }, [startId, locations, setVehiclePaths, findLocationById]);
  
  return {
    findLocationById
  };
}; 